import React, { useState, useEffect } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Bell, BellOff, BellRing, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import usePushNotifications from '@/hooks/usePushNotifications';

export const PushNotificationToggle = ({ onPermissionChange }) => {
  const {
    isSupported,
    permission,
    requestPermission,
    sendNotification,
    isEnabled
  } = usePushNotifications();

  const [testSent, setTestSent] = useState(false);

  const handleToggle = async (checked) => {
    if (checked && permission !== 'granted') {
      const granted = await requestPermission();
      if (granted) {
        toast.success('Push notifications enabled!');
        onPermissionChange?.(true);
      } else {
        toast.error('Push notification permission denied');
        onPermissionChange?.(false);
      }
    } else if (!checked) {
      toast.info('Push notifications disabled. You can re-enable them in your browser settings.');
      onPermissionChange?.(false);
    }
  };

  const handleTestNotification = () => {
    if (permission === 'granted') {
      sendNotification('Test Notification', {
        body: 'Push notifications are working correctly!',
        tag: 'test-notification',
        icon: '/favicon.ico'
      });
      setTestSent(true);
      toast.success('Test notification sent!');
      setTimeout(() => setTestSent(false), 3000);
    } else {
      toast.error('Please enable notifications first');
    }
  };

  if (!isSupported) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            Push Notifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-amber-700">
            Your browser does not support push notifications. Try using a modern browser like Chrome, Firefox, or Edge.
          </p>
        </CardContent>
      </Card>
    );
  }

  const getStatusIcon = () => {
    switch (permission) {
      case 'granted':
        return <BellRing className="w-5 h-5 text-green-500" />;
      case 'denied':
        return <BellOff className="w-5 h-5 text-red-500" />;
      default:
        return <Bell className="w-5 h-5 text-stone-400" />;
    }
  };

  const getStatusText = () => {
    switch (permission) {
      case 'granted':
        return 'Notifications are enabled';
      case 'denied':
        return 'Notifications are blocked';
      default:
        return 'Notifications not enabled';
    }
  };

  const getStatusColor = () => {
    switch (permission) {
      case 'granted':
        return 'text-green-600';
      case 'denied':
        return 'text-red-600';
      default:
        return 'text-stone-500';
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {getStatusIcon()}
          Push Notifications
        </CardTitle>
        <CardDescription>
          Receive instant alerts when someone searches your pet&apos;s microchip
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="push-toggle" className="text-sm font-medium">
              Enable Browser Notifications
            </Label>
            <p className={`text-xs ${getStatusColor()}`}>
              {getStatusText()}
            </p>
          </div>
          <Switch
            id="push-toggle"
            checked={permission === 'granted'}
            onCheckedChange={handleToggle}
            disabled={permission === 'denied'}
          />
        </div>
        
        {permission === 'denied' && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-xs text-red-700">
              <strong>Notifications are blocked.</strong> To enable them, click the lock icon in your browser&apos;s address bar and allow notifications for this site.
            </p>
          </div>
        )}

        {permission === 'granted' && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleTestNotification}
              disabled={testSent}
            >
              {testSent ? (
                <><CheckCircle2 className="w-4 h-4 mr-1 text-green-500" /> Sent!</>
              ) : (
                <><BellRing className="w-4 h-4 mr-1" /> Send Test Notification</>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PushNotificationToggle;
